import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api/client.ts";
import { AbTests } from "../components/AbTests.tsx";
import { toast } from "../components/feedback.tsx";
import { BrandBadge, Card, Empty, Kpi, StatusPill, TableSkeleton } from "../components/ui.tsx";
import { useUnsavedChanges } from "../components/unsaved.ts";
import { BroadcastDetail } from "./Campaigns.tsx";

interface Metrics {
  step_index?: number | null;
  recipients: number;
  pending: number;
  sent: number;
  delivered: number;
  failed: number;
  bounced: number;
  /** Deliberately not sent: opted out, left the audience, or Smart Sending. */
  skipped?: number;
  skipped_by?: Record<string, number>;
  opened: number;
  opened_automated_only: number;
  clicked: number;
  replied: number;
  unsubscribed: number;
  complained: number;
  openRate: number;
  clickRate: number;
  clickToOpenRate: number;
  replyRate: number;
  bounceRate: number;
  unsubscribeRate: number;
}

interface Report {
  campaign: {
    id: string;
    brand_id: string;
    name: string;
    type: "broadcast" | "sequence";
    status: string;
    created_at: string;
    scheduled_at: string | null;
    sent_at: string | null;
    brand_name: string;
    brand_badge: string;
    brand_color: string;
    sender_email: string;
    template_name: string | null;
    list_name: string | null;
    utm: UtmSettings | null;
  };
  totals: Metrics;
  steps: StepMetrics[];
  enrollments: Enrollments | null;
  links: { url: string; people: number; clicks: number }[];
}

/** A step of a sequence, including ones nobody has reached yet. */
interface StepMetrics extends Metrics {
  step_index: number;
  delay_hours: number;
  condition: "always" | "no_reply" | "no_open" | "no_click";
  template_name: string | null;
  template_subject: string | null;
  /** Active enrollments whose next message is this step. */
  waiting: number;
  next_due: string | null;
}

interface Enrollments {
  enrolled: number;
  active: number;
  completed: number;
  paused: number;
  failed: number;
  stopped: number;
  stopped_by: Record<string, number>;
}

interface Recipient {
  id: string;
  step_index: number | null;
  status: string;
  sent_at: string | null;
  failure_code: string | null;
  failure_detail: string | null;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  first_opened_at: string | null;
  first_clicked_at: string | null;
  replied_at: string | null;
  unsubscribed_at: string | null;
}

const FILTERS = [
  "all",
  "opened",
  "unopened",
  "clicked",
  "replied",
  "bounced",
  "failed",
  "skipped",
  "unsubscribed",
] as const;

/** Why a send was skipped, in words; mirrors SKIP_CODES on the server. */
const SKIP_REASONS: Record<string, string> = {
  SmartSending: "mailed recently (Smart Sending)",
  LeftList: "no longer in the audience",
  Suppressed: "unsubscribed or suppressed",
};
type Filter = (typeof FILTERS)[number];
const PAGE = 100;

/**
 * One campaign, start to finish. The dashboard answers "how is sending going
 * overall"; this answers "did this campaign work, and for whom".
 */
export function CampaignReportPage() {
  const { id = "" } = useParams();

  const report = useQuery({
    queryKey: ["campaign-report", id],
    queryFn: () => api.get<Report>(`/analytics/campaigns/${id}/report`),
    // Numbers move while it is going out and for a while after, as opens land.
    refetchInterval: (q) => (q.state.data?.campaign.status === "sending" ? 5000 : 30_000),
  });

  if (report.isError) {
    return (
      <div className="page">
        <Link to="/campaigns">&larr; Campaigns</Link>
        <Empty>That campaign doesn&rsquo;t exist, or was deleted.</Empty>
      </div>
    );
  }
  if (!report.data) return <div className="page" />;

  const { campaign, totals: t, steps, links } = report.data;
  const when = campaign.sent_at ?? campaign.scheduled_at;

  return (
    <div className="page">
      <Link to={campaign.type === "sequence" ? "/sequences" : "/campaigns"} className="back-link">
        &larr; {campaign.type === "sequence" ? "Sequences" : "Campaigns"}
      </Link>

      <div className="page-header report-header">
        <BrandBadge badge={campaign.brand_badge} color={campaign.brand_color} />
        <div>
          <h1>
            {campaign.name} <StatusPill status={campaign.status} />
          </h1>
          <p>
            {campaign.type} · {campaign.brand_name} · from{" "}
            <span className="mono">{campaign.sender_email}</span>
            {campaign.template_name && <> · template {campaign.template_name}</>}
            {campaign.list_name && <> · to {campaign.list_name}</>}
            {when && <> · {new Date(when).toLocaleString()}</>}
          </p>
        </div>
      </div>

      {campaign.type === "broadcast" && <BroadcastDetail id={campaign.id} />}

      <LinkTracking campaign={campaign} />

      <AbTests
        campaignId={campaign.id}
        brandId={campaign.brand_id}
        targets={
          campaign.type === "broadcast"
            ? [
                {
                  stepIndex: null,
                  title: "This broadcast",
                  baseSubject: null,
                  baseTemplate: campaign.template_name,
                },
              ]
            : steps.map((st) => ({
                stepIndex: st.step_index,
                title: `Step ${st.step_index + 1}`,
                baseSubject: st.template_subject,
                baseTemplate: st.template_name,
              }))
        }
      />

      {report.data.enrollments && <EnrollmentSummary e={report.data.enrollments} />}

      {/* A sequence's steps are worth seeing before anything has gone out:
          they are the plan, and who is waiting for what. */}
      {campaign.type === "sequence" && steps.length > 0 && <StepTable steps={steps} />}

      {t.recipients === 0 ? (
        <Card>
          <Empty>Nothing has been sent yet, so there is nothing to report.</Empty>
        </Card>
      ) : (
        <>
          <Headline t={t} />
          <PulseOutcomes campaignId={campaign.id} />
          {links.length > 0 && <LinkTable links={links} clicked={t.clicked} />}
          <Recipients
            campaignId={campaign.id}
            showStep={campaign.type === "sequence"}
            live={campaign.status === "sending"}
          />
        </>
      )}
    </div>
  );
}

/** The six numbers that say whether it worked, and what was left out of them. */
function Headline({ t }: { t: Metrics }) {
  return (
    <>
      <div className="kpi-grid">
        <Kpi
          label="Delivered"
          value={t.delivered.toLocaleString()}
          note={
            t.pending > 0
              ? `of ${t.sent.toLocaleString()} sent · ${t.pending.toLocaleString()} still going out`
              : `of ${t.sent.toLocaleString()} sent`
          }
        />
        <Kpi
          label="Opened"
          value={t.openRate}
          unit="%"
          note={`${t.opened.toLocaleString()} people`}
        />
        <Kpi
          label="Clicked"
          value={t.clickRate}
          unit="%"
          note={`${t.clicked.toLocaleString()} people · ${t.clickToOpenRate}% of openers`}
        />
        <Kpi
          label="Replied"
          value={t.replyRate}
          unit="%"
          note={`${t.replied.toLocaleString()} people`}
        />
        <Kpi
          label="Bounced"
          value={t.bounceRate}
          unit="%"
          note={`${t.bounced.toLocaleString()} addresses`}
          tone={t.bounceRate >= 2 ? "bad" : undefined}
        />
        <Kpi
          label="Unsubscribed"
          value={t.unsubscribeRate}
          unit="%"
          note={`${t.unsubscribed.toLocaleString()} people`}
          tone={t.unsubscribeRate >= 1 ? "bad" : undefined}
        />
      </div>

      {(t.opened_automated_only > 0 ||
        t.failed > 0 ||
        t.complained > 0 ||
        (t.skipped ?? 0) > 0) && (
        <p className="caveat" style={{ marginTop: -4 }}>
          {t.opened_automated_only > 0 && (
            <>
              {t.opened_automated_only.toLocaleString()} more were opened only by an automated
              reader (Apple Mail&rsquo;s privacy protection or a security scanner), so they
              aren&rsquo;t counted as opens.{" "}
            </>
          )}
          {t.failed > 0 && <>{t.failed.toLocaleString()} couldn&rsquo;t be sent at all. </>}
          {(t.skipped ?? 0) > 0 && <SkippedNote by={t.skipped_by ?? {}} total={t.skipped ?? 0} />}
          {t.complained > 0 && (
            <>
              {t.complained.toLocaleString()} marked it as spam; they won&rsquo;t be mailed again.{" "}
            </>
          )}
        </p>
      )}
    </>
  );
}

const CONDITION: Record<StepMetrics["condition"], string | null> = {
  always: null,
  no_reply: "only if no reply",
  no_open: "only if not opened",
  no_click: "only if no click",
};

function delay(hours: number): string {
  if (hours % 24 === 0) return `${hours / 24} day${hours === 24 ? "" : "s"}`;
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

/** When a step goes out, in the terms the sequence was built in. */
function timing(s: StepMetrics): string {
  if (s.step_index === 0) {
    return s.delay_hours === 0 ? "At enrollment" : `${delay(s.delay_hours)} after enrollment`;
  }
  return `${delay(s.delay_hours)} after step ${s.step_index}`;
}

function StepTable({ steps }: { steps: StepMetrics[] }) {
  const cell = (s: StepMetrics, n: number, rate?: number) => (
    <td className="num">
      {s.sent === 0 ? (
        <span style={{ color: "var(--gray-400)" }}>—</span>
      ) : (
        <>
          {n.toLocaleString()}
          {rate !== undefined && <span className="rate">{rate}%</span>}
        </>
      )}
    </td>
  );

  return (
    <Card title="Steps" subtitle="Every step, sent or still to come. Rates are of delivered.">
      <table>
        <thead>
          <tr>
            <th>Step</th>
            <th>Status</th>
            <th className="num">Sent</th>
            <th className="num">Delivered</th>
            <th className="num">Opened</th>
            <th className="num">Clicked</th>
            <th className="num">Replied</th>
            <th className="num">Bounced</th>
            <th className="num">Unsub.</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((s) => {
            const condition = CONDITION[s.condition];
            return (
              <tr key={s.step_index}>
                <td>
                  <div style={{ fontWeight: 600 }}>
                    Step {s.step_index + 1}
                    {s.template_name && (
                      <span style={{ fontWeight: 400, color: "var(--gray-500)" }}>
                        {" "}
                        · {s.template_name}
                      </span>
                    )}
                  </div>
                  <div style={{ color: "var(--gray-500)", fontSize: 11 }}>
                    {timing(s)}
                    {condition && <> · {condition}</>}
                  </div>
                </td>
                <td>
                  <StepStatus s={s} />
                </td>
                {cell(s, s.sent)}
                {cell(s, s.delivered)}
                {cell(s, s.opened, s.openRate)}
                {cell(s, s.clicked, s.clickRate)}
                {cell(s, s.replied, s.replyRate)}
                {cell(s, s.bounced, s.bounceRate)}
                {cell(s, s.unsubscribed, s.unsubscribeRate)}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

/** Sent, waiting, or not reached yet; a step part-way through is both. */
function StepStatus({ s }: { s: StepMetrics }) {
  if (s.waiting > 0) {
    return (
      <div>
        <span className="pill info">{s.waiting.toLocaleString()} waiting</span>
        {s.next_due && (
          <div style={{ color: "var(--gray-500)", fontSize: 11, marginTop: 2 }}>
            next {new Date(s.next_due).toLocaleString()}
          </div>
        )}
      </div>
    );
  }
  if (s.pending > 0) return <span className="pill info">going out</span>;
  if (s.sent > 0) return <span className="pill ok">sent</span>;
  return <span className="pill mute">not reached</span>;
}

/** The reasons the scheduler and the inbound route record, in plain words. */
const STOP_REASONS: Record<string, string> = {
  reply: "replied",
  click: "clicked",
  unsubscribed: "unsubscribed",
  "suppressed:hard_bounce": "bounced before",
  "suppressed:complaint": "marked spam before",
  "suppressed:manual": "suppressed",
  "suppressed:invalid_address": "invalid address",
};

/** Where everyone enrolled stands. */
function EnrollmentSummary({ e }: { e: Enrollments }) {
  if (e.enrolled === 0) {
    return (
      <p className="caveat" style={{ marginTop: 0 }}>
        Nobody is enrolled yet. Enroll a list from the Sequences tab.
      </p>
    );
  }
  const stopped = Object.entries(e.stopped_by)
    .map(([reason, n]) => `${n.toLocaleString()} ${STOP_REASONS[reason] ?? reason}`)
    .join(", ");
  return (
    <div className="enrollment-summary">
      <span>
        <strong>{e.enrolled.toLocaleString()}</strong> enrolled
      </span>
      <span>
        <strong>{e.active.toLocaleString()}</strong> active
      </span>
      <span>
        <strong>{e.completed.toLocaleString()}</strong> finished every step
      </span>
      {e.stopped > 0 && (
        <span>
          <strong>{e.stopped.toLocaleString()}</strong> stopped{stopped && ` (${stopped})`}
        </span>
      )}
      {e.paused > 0 && (
        <span>
          <strong>{e.paused.toLocaleString()}</strong> paused
        </span>
      )}
      {e.failed > 0 && (
        <span>
          <strong>{e.failed.toLocaleString()}</strong> failed
        </span>
      )}
    </div>
  );
}

function LinkTable({ links, clicked }: { links: Report["links"]; clicked: number }) {
  return (
    <Card
      title="Links"
      subtitle="Who clicked what. Security scanners following links are left out."
    >
      <table>
        <thead>
          <tr>
            <th>Link</th>
            <th className="num">People</th>
            <th className="num">Clicks</th>
          </tr>
        </thead>
        <tbody>
          {links.map((l) => (
            <tr key={l.url}>
              <td className="mono link-cell" title={l.url}>
                {l.url}
              </td>
              <td className="num">
                {l.people.toLocaleString()}
                {clicked > 0 && (
                  <span className="rate">{Math.round((l.people / clicked) * 100)}%</span>
                )}
              </td>
              <td className="num">{l.clicks.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/**
 * The furthest a message got, not merely whether it arrived: a recipient who
 * opened, clicked and replied read "delivered", with the engagement hidden in
 * the date columns beside it. Delivery failures outrank everything, since a
 * message that bounced never arrived to be read.
 */
function stage(r: Recipient): { label: string; tone: string } {
  if (r.status === "failed" && r.failure_code && r.failure_code in SKIP_REASONS) {
    return { label: "skipped", tone: "mute" };
  }
  if (r.status === "bounced" || r.status === "failed") return { label: r.status, tone: "bad" };
  // "submitted" is Postal's word for accepted but not yet confirmed delivered.
  if (r.sent_at === null) {
    return { label: r.status === "submitted" ? "sending" : r.status, tone: "mute" };
  }
  if (r.replied_at) return { label: "replied", tone: "best" };
  if (r.first_clicked_at) return { label: "clicked", tone: "ok" };
  if (r.first_opened_at) return { label: "opened", tone: "info" };
  return { label: "delivered", tone: "mute" };
}

function Recipients({
  campaignId,
  showStep,
  live,
}: {
  campaignId: string;
  showStep: boolean;
  live: boolean;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const [offset, setOffset] = useState(0);

  const rows = useQuery({
    queryKey: ["campaign-recipients", campaignId, filter, offset],
    queryFn: () =>
      api.get<{ total: number; rows: Recipient[] }>(
        `/analytics/campaigns/${campaignId}/recipients?filter=${filter}&limit=${PAGE}&offset=${offset}`,
      ),
    refetchInterval: live ? 5000 : false,
  });

  const total = rows.data?.total ?? 0;
  const date = (d: string | null) => (d ? new Date(d).toLocaleString() : "—");

  return (
    <Card
      title="Recipients"
      subtitle="What each person did. Opens and clicks exclude automated readers."
      action={
        <div className="tabs">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className={f === filter ? "active" : ""}
              onClick={() => {
                setFilter(f);
                setOffset(0);
              }}
            >
              {f}
            </button>
          ))}
        </div>
      }
    >
      {rows.isLoading ? (
        <TableSkeleton />
      ) : rows.data?.rows.length === 0 ? (
        <Empty>Nobody matches.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Recipient</th>
              {showStep && <th>Step</th>}
              <th>Status</th>
              <th>Sent</th>
              <th>Opened</th>
              <th>Clicked</th>
              <th>Replied</th>
            </tr>
          </thead>
          <tbody>
            {rows.data?.rows.map((r) => {
              const name = [r.first_name, r.last_name].filter(Boolean).join(" ");
              return (
                <tr key={r.id}>
                  <td>
                    <div className="mono">{r.email}</div>
                    {(name || r.company) && (
                      <div style={{ color: "var(--gray-500)", fontSize: 11 }}>
                        {[name, r.company].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </td>
                  {showStep && <td>{(r.step_index ?? 0) + 1}</td>}
                  <td>
                    <span className={`pill ${stage(r).tone}`} title={r.failure_detail ?? undefined}>
                      {stage(r).label}
                    </span>
                    {r.unsubscribed_at && (
                      <span className="pill warn" style={{ marginLeft: 4 }}>
                        unsubscribed
                      </span>
                    )}
                  </td>
                  <td className="mono">{date(r.sent_at)}</td>
                  <td className="mono">{date(r.first_opened_at)}</td>
                  <td className="mono">{date(r.first_clicked_at)}</td>
                  <td className="mono">{date(r.replied_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {total > PAGE && (
        <div className="card-body pager">
          <span>
            {offset + 1}&ndash;{Math.min(offset + PAGE, total)} of {total.toLocaleString()}
          </span>
          <button
            type="button"
            className="btn ghost small"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(offset - PAGE, 0))}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn ghost small"
            disabled={offset + PAGE >= total}
            onClick={() => setOffset(offset + PAGE)}
          >
            Next
          </button>
        </div>
      )}
    </Card>
  );
}

interface Outcomes {
  recipients: number;
  withLead: number;
  currentStages: { stage: string; n: number }[];
  moved: number;
  movedTo: { stage: string; n: number }[];
  trackingSince: string | null;
}

const stageName = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replaceAll("_", " ");

/**
 * Whether the deals moved, not just whether the mail was opened: of the
 * people this reached who have a Pulse lead, how many moved to a live stage
 * after it, and where everyone is now. Hidden when nobody it reached is in
 * Pulse.
 */
function PulseOutcomes({ campaignId }: { campaignId: string }) {
  const outcomes = useQuery({
    queryKey: ["pulse-outcomes", campaignId],
    queryFn: () => api.get<Outcomes>(`/analytics/campaigns/${campaignId}/pulse-outcomes`),
  });
  const o = outcomes.data;
  if (!o || o.withLead === 0) return null;
  const pct = (n: number) => (o.withLead ? Math.round((n / o.withLead) * 1000) / 10 : 0);

  return (
    <Card
      title="Pulse outcomes"
      subtitle={`${o.withLead.toLocaleString()} of ${o.recipients.toLocaleString()} people reached have a Pulse lead.${
        o.trackingSince
          ? ` Stage moves are recorded from ${new Date(o.trackingSince).toLocaleDateString()}.`
          : " Stage moves are recorded from the next sync."
      }`}
    >
      <div className="kpi-grid" style={{ padding: "14px 18px 0" }}>
        <Kpi
          label="Moved forward after mailing"
          value={o.moved.toLocaleString()}
          note={`${pct(o.moved)}% of those with a lead`}
          tone={o.moved > 0 ? "good" : undefined}
        />
      </div>
      <div className="outcome-cols">
        <div>
          <div className="label-mono">Moved to</div>
          {o.movedTo.length === 0 ? (
            <p className="hint">No moves recorded yet.</p>
          ) : (
            <StageBars rows={o.movedTo} total={o.moved} />
          )}
        </div>
        <div>
          <div className="label-mono">Where they are now</div>
          <StageBars rows={o.currentStages} total={o.withLead} />
        </div>
      </div>
    </Card>
  );
}

function StageBars({ rows, total }: { rows: { stage: string; n: number }[]; total: number }) {
  return (
    <ul className="stage-bars">
      {rows.map((r) => (
        <li key={r.stage}>
          <span className="stage-bar-label">{stageName(r.stage)}</span>
          <span className="stage-bar-track">
            <span
              className={`stage-bar-fill${r.stage === "lost" ? " lost" : ""}`}
              style={{ width: `${total ? Math.max((r.n / total) * 100, 2) : 0}%` }}
            />
          </span>
          <span className="num">{r.n.toLocaleString()}</span>
        </li>
      ))}
    </ul>
  );
}

interface UtmSettings {
  enabled: boolean;
  source?: string;
  medium?: string;
  campaign?: string;
}

/** Mirrors `slug` in @send/sending, so the default shown is the default sent. */
const slugify = (text: string) =>
  text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

/**
 * The UTM tags added to this campaign's links, so visits from it show up in
 * analytics under its name. On by default; each field falls back to the
 * value shown greyed out.
 */
function LinkTracking({
  campaign,
}: {
  campaign: { id: string; name: string; type: string; utm: UtmSettings | null };
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<UtmSettings>(() => utmForm(campaign.utm));
  const unsaved = JSON.stringify(form) !== JSON.stringify(utmForm(campaign.utm));
  useUnsavedChanges(unsaved);

  const save = useMutation({
    mutationFn: () => api.put(`/campaigns/${campaign.id}/utm`, form),
    onSuccess: () => {
      toast("Link tracking saved");
      void qc.invalidateQueries({ queryKey: ["campaign-report", campaign.id] });
    },
    onError: (err: Error) => toast(err.message, "bad"),
  });

  const defaults = { source: "send", medium: "email", campaign: slugify(campaign.name) };
  const example = exampleLink(form, defaults, campaign.type === "sequence");

  return (
    <Card
      title="Link tracking"
      subtitle="UTM tags added to every link, so visits show up in analytics under this campaign."
    >
      <div className="card-body">
        <label className="check">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          />{" "}
          Add UTM tags to links
        </label>
        {form.enabled && (
          <div className="row-3">
            {(["source", "medium", "campaign"] as const).map((k) => (
              <div className="field" key={k}>
                <label htmlFor={`utm-${k}`}>utm_{k}</label>
                <input
                  id={`utm-${k}`}
                  className="mono"
                  value={form[k] ?? ""}
                  placeholder={defaults[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}
          </div>
        )}
        <p className="hint" style={{ margin: "8px 0 12px" }}>
          {form.enabled ? (
            <>
              A link becomes <span className="mono">{example}</span>
              {campaign.type === "sequence" ? " (utm_content is the step)" : ""}. Links that already
              have UTM tags, and unsubscribe links, are left alone.
            </>
          ) : (
            "Links go out exactly as written in the template."
          )}
        </p>
        <button
          type="button"
          className="btn small"
          disabled={!unsaved || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : unsaved ? "Save" : "Saved"}
        </button>
      </div>
    </Card>
  );
}

/** The editable form of saved settings: every field present, blanks for defaults. */
function utmForm(saved: UtmSettings | null): UtmSettings {
  return {
    enabled: saved?.enabled ?? true,
    source: saved?.source ?? "",
    medium: saved?.medium ?? "",
    campaign: saved?.campaign ?? "",
  };
}

/** What a link to the site would become with these settings. */
function exampleLink(
  form: UtmSettings,
  defaults: { source: string; medium: string; campaign: string },
  sequence: boolean,
): string {
  const url = new URL("https://paykings.com/");
  url.searchParams.set("utm_source", form.source || defaults.source);
  url.searchParams.set("utm_medium", form.medium || defaults.medium);
  url.searchParams.set("utm_campaign", form.campaign || defaults.campaign);
  if (sequence) url.searchParams.set("utm_content", "step-1");
  return url.toString();
}

/** "12 skipped on purpose: 9 mailed recently (Smart Sending), 3 …" */
function SkippedNote({ by, total }: { by: Record<string, number>; total: number }) {
  const parts = Object.entries(by)
    .sort((a, b) => b[1] - a[1])
    .map(([code, n]) => `${n.toLocaleString()} ${SKIP_REASONS[code] ?? code}`);
  return (
    <>
      {total.toLocaleString()} skipped on purpose{parts.length ? `: ${parts.join(", ")}` : ""}. They
      aren&rsquo;t failures and aren&rsquo;t counted in the rates.{" "}
    </>
  );
}
